import { describeMutationError } from "@api/mutationErrors";
import { useSessionKey } from "@auth/sessionStore";
import { useUser } from "@context/UserContext";
import {
  Button,
  Chip,
  Dialog,
  FieldError,
  formatClockTime,
  IconButton,
  Input,
  SegmentedControl,
  Select,
  Tag,
} from "@scribenote/brian-design-system";
import { isLeadership } from "@users/userClient";
import { displayNameOf, useClinicUsers } from "@users/userQueries";
import type { TodayVisit } from "@visits/visitsClient";
import { useTodayVisitsQuery } from "@visits/visitsQueries";
import { type ReactNode, useState } from "react";

import { ObservationReceipt } from "./ObservationReceipt";
import {
  isManual,
  type ObservationIn,
  ObservationRefused,
  type ObservationsIn,
  type ObservationsReceipt,
  type RoomOccupant,
  type SlotKind,
  type SlotRole,
} from "./perceptionClient";
import { useRecordObservations } from "./perceptionQueries";
import { ago } from "./words";

/** Who a person added on the person step is: a staff member, or the owner or patient of one of today's visits. */
type AddWho = "staff" | "client" | "animal";

/** What one pending edit does. `set_*`/`remove_slot` name a slot Brian holds; `add_slot` makes a new one. */
type EditChange =
  | { op: "set_staff"; userUuid: string }
  | { op: "set_visit"; visitUuid: string }
  | { op: "remove_slot"; kind: "departure" | "retraction" }
  | {
      op: "add_slot";
      kind: "presence" | "correction";
      who: "staff";
      userUuid: string;
    }
  | {
      op: "add_slot";
      kind: "presence" | "correction";
      who: "client" | "animal";
      visitUuid: string;
    };

/**
 * One row of the batch. `row` is the slot id, or `new-N` for someone added.
 * The uuid is minted when the edit is first added and kept while the row has
 * an edit, so a resend after a failure reuses it.
 */
interface PendingEdit {
  row: string;
  observationUuid: string;
  /** ISO; when the person saw it. */
  observedAt: string;
  change: EditChange;
}

type Happened = "correct" | "left" | "never_here" | "arrived" | "already_here";

/** The person step's page; discarded on Back, turned into a `PendingEdit` on Add to changes. */
interface PersonDraft {
  /** Null for someone the camera missed. */
  occupant: RoomOccupant | null;
  who: AddWho | null;
  /** `user:<uuid>` or `visit:<uuid>`. */
  identity: string | null;
  happened: Happened | null;
  /** A `datetime-local` value. */
  when: string;
}

const ADD_SLOT: Record<AddWho, { slotKind: SlotKind; role: SlotRole }> = {
  staff: { slotKind: "person", role: "staff" },
  client: { slotKind: "person", role: "client" },
  animal: { slotKind: "animal", role: "animal" },
};

const ADD_WHO_OPTIONS: { value: AddWho; label: string }[] = [
  { value: "staff", label: "Staff" },
  { value: "client", label: "Owner on a visit" },
  { value: "animal", label: "Patient on a visit" },
];

const USER = "user:";
const VISIT = "visit:";

const currentIdentity = (occupant: RoomOccupant) =>
  occupant.nameRef
    ? USER + occupant.nameRef
    : occupant.visitUuid
      ? VISIT + occupant.visitUuid
      : null;

/** The edit a finished page makes, or null while a choice is missing. */
const changeOf = (draft: PersonDraft): EditChange | null => {
  const { occupant, identity, happened } = draft;
  if (occupant) {
    switch (happened) {
      case "left":
        return { op: "remove_slot", kind: "departure" };
      case "never_here":
        return { op: "remove_slot", kind: "retraction" };
      case "correct":
        if (!identity || identity === currentIdentity(occupant)) return null;
        return identity.startsWith(USER)
          ? { op: "set_staff", userUuid: identity.slice(USER.length) }
          : { op: "set_visit", visitUuid: identity.slice(VISIT.length) };
      default:
        return null;
    }
  }
  const kind =
    happened === "arrived"
      ? "presence"
      : happened === "already_here"
        ? "correction"
        : null;
  if (!draft.who || !identity || !kind) return null;
  if (draft.who === "staff")
    return identity.startsWith(USER)
      ? {
          op: "add_slot",
          kind,
          who: "staff",
          userUuid: identity.slice(USER.length),
        }
      : null;
  return identity.startsWith(VISIT)
    ? {
        op: "add_slot",
        kind,
        who: draft.who,
        visitUuid: identity.slice(VISIT.length),
      }
    : null;
};

const toObservation = ({
  row,
  observationUuid,
  observedAt,
  change,
}: PendingEdit): ObservationIn => {
  const base = { observation_uuid: observationUuid, observed_at: observedAt };
  switch (change.op) {
    case "set_staff":
      return {
        ...base,
        op: "set_staff",
        kind: "correction",
        slot_id: row,
        user_uuid: change.userUuid,
      };
    case "set_visit":
      return {
        ...base,
        op: "set_visit",
        kind: "correction",
        slot_id: row,
        visit_uuid: change.visitUuid,
      };
    case "remove_slot":
      return { ...base, op: "remove_slot", kind: change.kind, slot_id: row };
    case "add_slot": {
      const { slotKind, role } = ADD_SLOT[change.who];
      return {
        ...base,
        op: "add_slot",
        kind: change.kind,
        slot_kind: slotKind,
        role,
        ...(change.who === "staff"
          ? { user_uuid: change.userUuid }
          : { visit_uuid: change.visitUuid }),
      };
    }
  }
};

/** Every pending edit becomes one observation with its own time; brian applies them in this order in one transaction. */
const buildObservationsBody = (
  edits: readonly PendingEdit[],
): ObservationsIn => ({
  source: "admin_ui",
  observations: edits.map(toObservation),
});

/** brian bounds each `observed_at` to [now - 1 h, now + 60 s]; checked here so the page says so before a 422. */
const EARLIEST_MS = 60 * 60 * 1000;
const LATEST_MS = 60 * 1000;

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalInput = (ms: number) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const fromLocalInput = (value: string): number | null => {
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};
const timeProblem = (ms: number | null, now: number) =>
  ms === null
    ? "Choose when you saw it."
    : ms < now - EARLIEST_MS
      ? "Brian takes corrections from the last hour only."
      : ms > now + LATEST_MS
        ? "That is in the future."
        : null;

const REFUSALS: Record<string, string> = {
  payload_mismatch:
    "This change was already recorded with different details. Remove it and add it again.",
  raced: "The camera updated the room at the same moment. Send again.",
  visit_outside_window:
    "This visit is outside the window Brian is tracking right now (about 14 h back to 3 h ahead); pick another or wait until it is closer.",
  visit_not_found: "Brian can't see this visit.",
  user_not_found: "Brian can't see this staff member.",
  room_not_found: "This room is gone or inactive.",
  observed_at_out_of_range:
    "That time is outside the last hour. Open the row and change it.",
};
const refusalWord = (reason: string) =>
  REFUSALS[reason] ??
  `${reason.charAt(0).toUpperCase()}${reason.slice(1).replace(/_/g, " ")}.`;

type Users = { id: string; displayName: string }[];

const visitTime = (visit: TodayVisit) =>
  visit.scheduledStart ? formatClockTime(new Date(visit.scheduledStart)) : null;

const visitLabel = (visit: TodayVisit, role: string) =>
  (role === "client"
    ? [visit.clientName ?? "No client", visit.patientName]
    : [visit.patientName, visit.clientName]
  )
    .concat(visitTime(visit))
    .filter(Boolean)
    .join(" · ");

/** A client or animal is tied to a visit; brian never puts their names on the slot. */
const visitWord = (
  visitUuid: string,
  role: string,
  visits: TodayVisit[] | undefined,
) => {
  const visit = visits?.find((v) => v.id === visitUuid);
  if (!visit) return "On a visit not on today’s list";
  return role === "animal"
    ? `${visit.patientName}${visit.clientName ? ` (${visit.clientName})` : ""}`
    : `${visit.clientName ?? "Client"} (${visit.patientName})`;
};

const identityWord = (
  occupant: RoomOccupant,
  visits: TodayVisit[] | undefined,
) => {
  if (occupant.displayName) return occupant.displayName;
  if (occupant.visitUuid)
    return visitWord(
      occupant.visitUuid,
      occupant.kind === "animal" ? "animal" : "client",
      visits,
    );
  return "Unnamed";
};

const roleWord = (kind: string, role: string) =>
  role === "unknown" || role === kind ? kind : `${role} · ${kind}`;

const HAPPENED_WORD: Record<ObservationIn["kind"], string> = {
  presence: "arrived",
  correction: "here already",
  departure: "left",
  retraction: "never here",
};

const clock = (iso: string) => formatClockTime(new Date(iso));

/** One line for a pending edit, such as "→ Mochi · arrived 14:02". */
const editSummary = (
  edit: PendingEdit,
  occupant: RoomOccupant | undefined,
  users: Users,
  visits: TodayVisit[],
) => {
  const { change } = edit;
  const at = clock(edit.observedAt);
  switch (change.op) {
    case "set_staff":
      return `→ ${displayNameOf(users, change.userUuid, "Staff member")} · corrected ${at}`;
    case "set_visit":
      return `→ ${visitWord(change.visitUuid, occupant?.kind === "animal" ? "animal" : "client", visits)} · corrected ${at}`;
    case "remove_slot":
      return `→ ${HAPPENED_WORD[change.kind]} ${at}`;
    case "add_slot":
      return `→ ${
        change.who === "staff"
          ? displayNameOf(users, change.userUuid, "Staff member")
          : visitWord(change.visitUuid, change.who, visits)
      } · ${HAPPENED_WORD[change.kind]} ${at}`;
  }
};

const statusTone = (status: string) =>
  status === "candidate" ? "attention" : "recorded";

type Picker = "staff" | "visit" | "either" | "none";

const pickerFor = (kind: string, role: string): Picker => {
  if (role === "staff") return "staff";
  if (role === "client" || role === "animal" || kind === "animal")
    return "visit";
  if (kind === "person") return "either";
  return "none";
};

const IdentitySelect = ({
  picker,
  role,
  value,
  onPick,
  users,
  visits,
  error,
}: {
  picker: Picker;
  role: string;
  value: string | null;
  onPick: (value: string) => void;
  users: Users;
  visits: TodayVisit[];
  error?: string;
}) => {
  if (picker === "none")
    return (
      <p className="text-sm text-annotation">
        Brian does not name this kind of slot.
      </p>
    );
  const staff = users.map((u) => ({
    value: USER + u.id,
    label: picker === "either" ? `Staff · ${u.displayName}` : u.displayName,
  }));
  const onVisit = visits
    .filter((v) => !v.cancelledAt)
    .map((v) => ({
      value: VISIT + v.id,
      label:
        picker === "either"
          ? `Client · ${visitLabel(v, "client")}`
          : visitLabel(v, role),
    }));
  const options =
    picker === "staff"
      ? staff
      : picker === "visit"
        ? onVisit
        : [...onVisit, ...staff];
  return (
    <Select
      label="Who this is"
      value={value}
      onValueChange={onPick}
      placeholder={
        picker === "staff"
          ? "Choose a staff member"
          : picker === "visit"
            ? "Choose today’s visit"
            : "Choose a visit or staff member"
      }
      options={options}
      error={error}
    />
  );
};

const Radios = ({
  name,
  legend,
  value,
  options,
  onChange,
  error,
}: {
  name: string;
  legend: string;
  value: Happened | null;
  options: {
    value: Happened;
    label: string;
    hint: string;
    disabled?: boolean;
  }[];
  onChange: (value: Happened) => void;
  error: string | null;
}) => (
  <fieldset className="flex flex-col gap-1.5">
    <legend className="mb-1 text-sm font-semibold text-ink">{legend}</legend>
    {options.map((option) => (
      <label
        key={option.value}
        className="flex cursor-pointer items-start gap-2 rounded border border-rule px-3 py-2 text-sm has-[:checked]:border-accent has-[:checked]:bg-wall has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
      >
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          disabled={option.disabled}
          onChange={() => onChange(option.value)}
          className="mt-1 accent-accent"
        />
        <span className="flex flex-col">
          <span className="text-ink">{option.label}</span>
          <span className="text-fine-print text-annotation">{option.hint}</span>
        </span>
      </label>
    ))}
    <FieldError>{error}</FieldError>
  </fieldset>
);

const OccupantLine = ({
  occupant,
  visits,
}: {
  occupant: RoomOccupant;
  visits: TodayVisit[];
}) => (
  <span className="flex flex-wrap items-baseline gap-2">
    <span className="font-mono text-fine-print text-annotation">
      {occupant.slot}
    </span>
    <span className="text-sm text-annotation">
      {roleWord(occupant.kind, occupant.role)}
    </span>
    <span className="text-sm text-ink-body">
      {identityWord(occupant, visits)}
    </span>
    {occupant.nameStatus !== "none" && (
      <Chip tone={statusTone(occupant.nameStatus)} size="sm">
        name {occupant.nameStatus}
      </Chip>
    )}
    {occupant.visitStatus !== "none" && (
      <Chip tone={statusTone(occupant.visitStatus)} size="sm">
        visit {occupant.visitStatus}
      </Chip>
    )}
    {isManual(occupant) && <Tag>manual</Tag>}
  </span>
);

const PendingLine = ({
  summary,
  error,
  disabled,
  onRemove,
}: {
  summary: string;
  error: string | null;
  disabled: boolean;
  onRemove: () => void;
}) => (
  <>
    <span className="flex flex-wrap items-center gap-2">
      <Chip tone="brian" size="sm">
        pending
      </Chip>
      <span className="text-sm text-ink">{summary}</span>
      <Button variant="ghost" size="sm" disabled={disabled} onClick={onRemove}>
        Remove edit
      </Button>
    </span>
    <FieldError>{error}</FieldError>
  </>
);

/** A row the person can tap; the Remove edit button sits outside the tap target. */
const RowButton = ({
  label,
  disabled,
  onOpen,
  children,
}: {
  label: string;
  disabled: boolean;
  onOpen: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    disabled={disabled}
    onClick={onOpen}
    className="flex w-full flex-col gap-1 rounded px-1 py-1 text-left hover:bg-smudge disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const ListStep = ({
  roster,
  edits,
  users,
  visits,
  now,
  pending,
  rowError,
  formError,
  onOpen,
  onRemove,
}: {
  roster: RoomOccupant[];
  edits: PendingEdit[];
  users: Users;
  visits: TodayVisit[];
  now: number;
  pending: boolean;
  rowError: (edit: PendingEdit) => string | null;
  formError: string | null;
  onOpen: (row: string | null) => void;
  onRemove: (row: string) => void;
}) => {
  const added = edits.filter((e) => e.change.op === "add_slot");
  return (
    <div data-dd-privacy="hidden" className="flex flex-col gap-3">
      <section aria-label="Who Brian thinks is here">
        <h3 className="text-sm font-semibold text-ink">
          Who Brian thinks is here
        </h3>
        {roster.length === 0 && added.length === 0 ? (
          <p className="py-2 text-sm text-annotation">Nobody.</p>
        ) : (
          <ul className="flex flex-col">
            {roster.map((occupant) => {
              const edit = edits.find((e) => e.row === occupant.slot);
              const seen = ago(occupant.lastSeenAt, now);
              return (
                <li
                  key={occupant.slot}
                  className="flex flex-col gap-1 border-b border-rule py-2 last:border-b-0"
                >
                  <RowButton
                    label={`Change ${occupant.slot}`}
                    disabled={pending}
                    onOpen={() => onOpen(occupant.slot)}
                  >
                    <OccupantLine occupant={occupant} visits={visits} />
                    <span className="font-mono text-fine-print text-annotation">
                      {seen ? `seen ${seen}` : "not seen"} · {occupant.misses}{" "}
                      missed
                    </span>
                  </RowButton>
                  {edit && (
                    <PendingLine
                      summary={editSummary(edit, occupant, users, visits)}
                      error={rowError(edit)}
                      disabled={pending}
                      onRemove={() => onRemove(edit.row)}
                    />
                  )}
                </li>
              );
            })}
            {added.map((edit) => (
              <li
                key={edit.row}
                className="flex flex-col gap-1 border-b border-rule py-2 last:border-b-0"
              >
                <RowButton
                  label={`Change ${edit.row}`}
                  disabled={pending}
                  onOpen={() => onOpen(edit.row)}
                >
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span className="font-mono text-fine-print text-annotation">
                      {edit.row}
                    </span>
                    {edit.change.op === "add_slot" && (
                      <span className="text-sm text-annotation">
                        {roleWord(
                          ADD_SLOT[edit.change.who].slotKind,
                          ADD_SLOT[edit.change.who].role,
                        )}
                      </span>
                    )}
                    <span className="text-sm text-ink-body">
                      Not on the camera’s list
                    </span>
                  </span>
                </RowButton>
                <PendingLine
                  summary={editSummary(edit, undefined, users, visits)}
                  error={rowError(edit)}
                  disabled={pending}
                  onRemove={() => onRemove(edit.row)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
      <Button
        variant="outline"
        size="sm"
        className="self-start"
        disabled={pending}
        onClick={() => onOpen(null)}
      >
        Add someone the camera missed
      </Button>
      <FieldError>{formError}</FieldError>
    </div>
  );
};

const PersonStep = ({
  draft,
  tried,
  users,
  visits,
  now,
  refusal,
  onChange,
}: {
  draft: PersonDraft;
  tried: boolean;
  users: Users;
  visits: TodayVisit[];
  now: number;
  refusal: string | null;
  onChange: (draft: PersonDraft) => void;
}) => {
  const { occupant } = draft;
  const current = occupant ? currentIdentity(occupant) : null;
  const changed = !!occupant && draft.identity !== current;
  const whenProblem = timeProblem(fromLocalInput(draft.when), now);
  const slot = draft.who ? ADD_SLOT[draft.who] : null;
  const picker: Picker | null = occupant
    ? pickerFor(occupant.kind, occupant.role)
    : slot
      ? pickerFor(slot.slotKind, slot.role)
      : null;

  const pick = (identity: string) => {
    if (!occupant) return onChange({ ...draft, identity });
    // Picking someone else means "correct who this is"; picking them back undoes it.
    const happened: Happened | null =
      identity !== current
        ? "correct"
        : draft.happened === "correct"
          ? null
          : draft.happened;
    onChange({ ...draft, identity, happened });
  };
  const choose = (happened: Happened) =>
    onChange(
      happened === "left" || happened === "never_here"
        ? { ...draft, happened, identity: current }
        : { ...draft, happened },
    );

  return (
    <div data-dd-privacy="hidden" className="flex flex-col gap-4">
      <section aria-label="Who is this" className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-ink">Who is this</h3>
        {!occupant && (
          <SegmentedControl
            aria-label="Who they are"
            size="sm"
            value={draft.who ?? ""}
            onValueChange={(who) =>
              onChange({ ...draft, who: who as AddWho, identity: null })
            }
            options={ADD_WHO_OPTIONS}
          />
        )}
        {!occupant && tried && !draft.who && (
          <FieldError>Choose staff, an owner or a patient.</FieldError>
        )}
        {picker && (
          <IdentitySelect
            picker={picker}
            role={occupant ? occupant.role : (slot?.role ?? "unknown")}
            value={draft.identity}
            onPick={pick}
            users={users}
            visits={visits}
            error={
              tried && !occupant && draft.who && !draft.identity
                ? "Choose who this is."
                : undefined
            }
          />
        )}
      </section>
      <Radios
        name="what-happened"
        legend="What happened"
        value={draft.happened}
        onChange={choose}
        error={tried && !draft.happened ? "Choose what happened." : null}
        options={
          occupant
            ? [
                {
                  value: "correct",
                  label: "Correct who this is",
                  hint: changed
                    ? "Brian had the wrong person."
                    : "Pick someone else above first.",
                  disabled: !changed,
                },
                {
                  value: "left",
                  label: "Left",
                  hint: "They were here and walked out.",
                },
                {
                  value: "never_here",
                  label: "Never here",
                  hint: "The camera was wrong; nobody was there.",
                },
              ]
            : [
                {
                  value: "arrived",
                  label: "Arrived",
                  hint: "They walked in.",
                },
                {
                  value: "already_here",
                  label: "Was here already",
                  hint: "The camera missed someone who was already in.",
                },
              ]
        }
      />
      <Input
        type="datetime-local"
        step={1}
        label="When"
        hint="When you saw it. Defaults to now; within the last hour."
        value={draft.when}
        min={toLocalInput(now - EARLIEST_MS)}
        max={toLocalInput(now + LATEST_MS)}
        onChange={(e) => onChange({ ...draft, when: e.target.value })}
        error={whenProblem ?? undefined}
      />
      <FieldError>{refusal}</FieldError>
    </div>
  );
};

type Step =
  | { at: "list" }
  | { at: "person"; row: string | null; draft: PersonDraft; tried: boolean }
  | { at: "receipt"; receipt: ObservationsReceipt; sent: ObservationIn[] };

const draftFor = (
  occupant: RoomOccupant | null,
  edit: PendingEdit | undefined,
  now: number,
): PersonDraft => {
  const when = toLocalInput(edit ? Date.parse(edit.observedAt) : now);
  const change = edit?.change;
  if (occupant) {
    const current = currentIdentity(occupant);
    switch (change?.op) {
      case "set_staff":
        return {
          occupant,
          who: null,
          identity: USER + change.userUuid,
          happened: "correct",
          when,
        };
      case "set_visit":
        return {
          occupant,
          who: null,
          identity: VISIT + change.visitUuid,
          happened: "correct",
          when,
        };
      case "remove_slot":
        return {
          occupant,
          who: null,
          identity: current,
          happened: change.kind === "departure" ? "left" : "never_here",
          when,
        };
      default:
        return { occupant, who: null, identity: current, happened: null, when };
    }
  }
  if (change?.op === "add_slot")
    return {
      occupant: null,
      who: change.who,
      identity:
        change.who === "staff"
          ? USER + change.userUuid
          : VISIT + change.visitUuid,
      happened: change.kind === "presence" ? "arrived" : "already_here",
      when,
    };
  return { occupant: null, who: null, identity: null, happened: null, when };
};

/**
 * Tell Brian who is in a room when the camera got it wrong (BRI-178). The
 * list step shows the roster as it was when the drawer opened (then as brian
 * returns it after a save), so polling cannot move a row mid-edit. Tapping a
 * row, or Add, opens the person step: who this is, what happened, and when.
 * Add to changes puts one edit per row in the batch; Record sends every edit
 * as one observation, each with its own `observed_at`, in one request to
 * `POST /perception/rooms/{room}/observations`, applied in one transaction.
 */
export const CorrectDrawer = ({
  roomId,
  roomName,
  occupants,
  now,
  onClose,
}: {
  roomId: string;
  roomName: string;
  occupants: RoomOccupant[];
  now: number;
  onClose: () => void;
}) => {
  const sessionKey = useSessionKey();
  const { currentUser } = useUser();
  const users = useClinicUsers(sessionKey).data ?? [];
  const visits = useTodayVisitsQuery(sessionKey, true).data ?? [];
  const record = useRecordObservations(sessionKey, roomId);
  const [roster, setRoster] = useState(occupants);
  const [edits, setEdits] = useState<PendingEdit[]>([]);
  const [nextNew, setNextNew] = useState(1);
  const [step, setStep] = useState<Step>({ at: "list" });

  const pending = record.isPending;
  const count = edits.length;
  const refused =
    record.error instanceof ObservationRefused ? record.error : null;
  const staleEdit = (edit: PendingEdit) =>
    timeProblem(Date.parse(edit.observedAt), now)
      ? "That time is now more than an hour ago. Open the row and change it."
      : null;
  const rowError = (edit: PendingEdit) =>
    refused?.observationUuid === edit.observationUuid
      ? refusalWord(refused.reason)
      : staleEdit(edit);
  const matched =
    !!refused &&
    edits.some((edit) => edit.observationUuid === refused.observationUuid);
  const formError = !record.error
    ? null
    : refused
      ? matched
        ? null
        : refusalWord(refused.reason)
      : describeMutationError(record.error, "record the changes");
  const anyStale = edits.some((edit) => staleEdit(edit) !== null);

  const open = (row: string | null) => {
    const edit = row ? edits.find((e) => e.row === row) : undefined;
    const occupant = row ? (roster.find((o) => o.slot === row) ?? null) : null;
    setStep({
      at: "person",
      row,
      draft: draftFor(occupant, edit, now),
      tried: false,
    });
  };

  const removeEdit = (row: string) => {
    record.reset();
    setEdits((all) => all.filter((e) => e.row !== row));
  };

  const addToChanges = () => {
    if (step.at !== "person") return;
    const change = changeOf(step.draft);
    const at = fromLocalInput(step.draft.when);
    if (!change || at === null || timeProblem(at, now)) {
      setStep({ ...step, tried: true });
      return;
    }
    const row = step.row ?? `new-${nextNew}`;
    if (!step.row) setNextNew((n) => n + 1);
    const observedAt = new Date(at).toISOString();
    record.reset();
    setEdits((all) => {
      const existing = all.find((e) => e.row === row);
      return existing
        ? all.map((e) => (e.row === row ? { ...e, observedAt, change } : e))
        : [
            ...all,
            { row, observationUuid: crypto.randomUUID(), observedAt, change },
          ];
    });
    setStep({ at: "list" });
  };

  const submit = () => {
    if (count === 0 || anyStale) return;
    const body = buildObservationsBody(edits);
    record.mutate(body, {
      onSuccess: (result) => {
        setRoster(result.occupants);
        setEdits([]);
        setStep({ at: "receipt", receipt: result, sent: body.observations });
      },
    });
  };

  const personEdit =
    step.at === "person" && step.row
      ? edits.find((e) => e.row === step.row)
      : undefined;

  const title =
    step.at === "person" ? (
      <span className="flex items-center gap-2">
        <IconButton
          variant="ghost"
          size="sm"
          aria-label="Back to the list"
          onClick={() => setStep({ at: "list" })}
        >
          ←
        </IconButton>
        <span data-dd-privacy="hidden" className="truncate">
          {step.draft.occupant
            ? `${step.draft.occupant.slot} · ${identityWord(step.draft.occupant, visits)}`
            : step.row
              ? `${step.row} · someone the camera missed`
              : "Someone the camera missed"}
        </span>
      </span>
    ) : (
      `Who's in ${roomName}`
    );

  const footer =
    step.at === "person" ? (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={() => setStep({ at: "list" })}>
          Back
        </Button>
        <Button variant="accent" onClick={addToChanges}>
          {personEdit ? "Update change" : "Add to changes"}
        </Button>
      </div>
    ) : step.at === "receipt" ? (
      <Button variant="accent" onClick={onClose}>
        Done
      </Button>
    ) : (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {count === 0 ? (
          <span className="mr-auto text-sm text-annotation">
            No changes yet
          </span>
        ) : null}
        <Button variant="ghost" disabled={pending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="accent"
          disabled={pending || count === 0 || anyStale}
          onClick={submit}
        >
          Record {count} {count === 1 ? "change" : "changes"}
        </Button>
      </div>
    );

  return (
    <Dialog
      open
      onClose={onClose}
      // A stray Escape or backdrop click must not drop a batch or a half-filled page.
      dismissable={step.at === "receipt" || (step.at === "list" && count === 0)}
      size="md"
      title={title}
      footer={footer}
    >
      {step.at === "receipt" ? (
        <div data-dd-privacy="hidden">
          <ObservationReceipt
            receipt={step.receipt}
            sent={step.sent}
            leadership={isLeadership(currentUser)}
          />
        </div>
      ) : step.at === "person" ? (
        <PersonStep
          draft={step.draft}
          tried={step.tried}
          users={users}
          visits={visits}
          now={now}
          refusal={personEdit ? rowError(personEdit) : null}
          onChange={(draft) => setStep({ ...step, draft })}
        />
      ) : (
        <ListStep
          roster={roster}
          edits={edits}
          users={users}
          visits={visits}
          now={now}
          pending={pending}
          rowError={rowError}
          formError={formError}
          onOpen={open}
          onRemove={removeEdit}
        />
      )}
    </Dialog>
  );
};
